import { Link } from 'react-router-dom';
import { Star, Heart, MapPin } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VendorCard = ({ vendor, onFavoriteChange }) => {
  const { user, token, isAuthenticated, updateFavorites } = useAuth();
  const isFavorited = user?.favorites?.includes(vendor.id);
  const cardImage = vendor.cover_image
    || vendor.images?.[0]
    || 'https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/ixkrplkk_1971-gersonana-websized-319%20copy.jpg';

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please login to save favourites');
      return;
    }

    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${vendor.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateFavorites(user.favorites.filter(id => id !== vendor.id));
        toast.success('Removed from favourites');
      } else {
        await axios.post(`${API}/favorites/${vendor.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateFavorites([...user.favorites, vendor.id]);
        toast.success('Added to favourites');
      }
      if (onFavoriteChange) onFavoriteChange();
    } catch (error) {
      toast.error('Failed to update favourites');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < Math.floor(rating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-stone-300'}`}
      />
    ));
  };

  // Use slug for SEO-friendly URL, fallback to id for backwards compatibility
  const vendorUrl = vendor.slug ? `/vendors/${vendor.slug}` : `/vendors/${vendor.id}`;

  return (
    <Link to={vendorUrl} className="block group" data-testid={`vendor-card-${vendor.id}`}>
      <article className="vendor-card rounded-sm overflow-hidden">
        {/* Image */}
        <div className="relative image-zoom aspect-[4/5] overflow-hidden" data-testid={`vendor-card-gallery-${vendor.id}`}>
          <img
            src={cardImage}
            alt={`${vendor.name} cover image`}
            className="absolute inset-0 h-full w-full object-cover grayscale-[10%] group-hover:grayscale-0"
            data-testid={`vendor-card-cover-image-${vendor.id}`}
          />

          {vendor.special_offer_text && (
            <span className="absolute top-4 left-4 max-w-[80%] bg-[#D4AF37] text-white px-3 py-2 text-xs uppercase tracking-[0.18em] font-medium rounded-full shadow-lg" data-testid={`vendor-offer-banner-${vendor.id}`}>
              {vendor.special_offer_text}
            </span>
          )}
          {/* Featured Badge */}
          {vendor.is_featured && (
            <span className={`absolute ${vendor.special_offer_text ? 'top-16' : 'top-4'} left-4 bg-[#5C0120] text-white px-3 py-1 text-xs uppercase tracking-widest font-medium`}>
              Featured
            </span>
          )}
          {/* Favorite Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 bg-white/80 hover:bg-white rounded-full"
            onClick={handleFavorite}
            data-testid={`favorite-btn-${vendor.id}`}
          >
            <Heart
              className={`h-5 w-5 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-stone-600'}`}
            />
          </Button>
          {/* Category Badge(s) */}
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-1">
            {(vendor.categories?.length > 0 ? vendor.categories : [vendor.category]).map((cat, idx) => (
              <span 
                key={idx}
                className="bg-white/90 text-[#1C1917] px-2 py-1 text-xs uppercase tracking-widest font-medium"
              >
                {cat}
              </span>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-3">
          <h3 className="heading-md text-[#1C1917] group-hover:text-[#5C0120] transition-colors duration-200">
            {vendor.name}
          </h3>
          {vendor.special_offer_code && (
            <p className="text-xs uppercase tracking-[0.18em] text-[#5C0120]">Offer: {vendor.special_offer_code}</p>
          )}
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {renderStars(vendor.average_rating)}
            </div>
            <span className="text-sm text-stone-500">
              ({vendor.review_count} reviews)
            </span>
          </div>

          <p className="text-stone-600 text-sm leading-relaxed whitespace-pre-line break-words line-clamp-3" data-testid={`vendor-card-description-${vendor.id}`}>
            {vendor.description}
          </p>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-1 text-stone-500 text-sm">
              <MapPin className="h-4 w-4" />
              <span className="truncate max-w-[150px]">{vendor.address}</span>
            </div>
            <span className="text-[#5C0120] font-medium">{vendor.price_range}</span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default VendorCard;
